import React, { useEffect, useRef, useState } from "react";
import { Calendar, momentLocalizer } from "react-big-calendar";
import withDragAndDrop from "react-big-calendar/lib/addons/dragAndDrop";
import "react-big-calendar/lib/addons/dragAndDrop/styles.css";
import { StyledAppCalendar } from "./styles";
import moment from "moment";
import { CalendarGroupingEnum, CalendarSpanEnum, ReservationConflict, HolidayType, ReservationOccurrence } from "src/types";
import { Dialog, DialogContent } from "@mui/material";
import ReservationForm from "./ReservationForm";
import { useCalendarDragAndDrop } from "src/Utils/Hooks/useScheduleDragAndDrop";
import { selectAuth, selectRooms, selectTypes, useAppSelector } from "@redux";
import { ReservationInfo } from "./ReservationInfo";
import { makeCustomAgendaDate, makeCustomAgendaEvent } from "./FrontEndHelpers";
import { domGroupWeekly } from './domGroupWeekly';
import { domGroupDailyNamesHeader } from './domGroupDailyNamesHeader';
import { domTimeHeaderSticky2 } from './domTimeHeaderSticky2';
import { ReactComponent as IconRepeat } from '@assets/Icons/IconRepeat.svg';
import { detectConflict } from "./detectConflict";
import PrivateEventMark from "../Common/PrivateEventMark";

const localizer = momentLocalizer(moment);

// Create a drag and drop calendar component
const DragAndDropCalendar = withDragAndDrop<ReservationOccurrence>(Calendar);

export const ScheduleCalendar = ({
  calenderUserId,
  facilityId,
  currentDate,
  resources,
  events,
  span,
  grouping,
  holidays,
  defaultParticipants,
  ownCalendar,
  name,
  instanceKey,
  calendarIndex = 1,
  onNavigateRequest,
  dailyGouping = false,
  refreshReservations,
  defaultParticipantsHistory,
  storeDefaultParticipantsSelection,
  openEventRequest,
}: {
  calenderUserId: number,
  facilityId?: number,
  currentDate: Date,
  resources?: {
    id: number | undefined;
    title: string;
  }[],
  events: ReservationOccurrence[],
  span: CalendarSpanEnum,
  grouping: CalendarGroupingEnum,
  holidays: string[],
  defaultParticipants: number[],
  ownCalendar: boolean,
  name: string,
  instanceKey: string,
  calendarIndex?: number,
  onNavigateRequest: (date: Date, view: CalendarSpanEnum, action?: string) => void;
  dailyGouping: boolean;
  refreshReservations: () => void;
  defaultParticipantsHistory: any[],
  storeDefaultParticipantsSelection: (body: any) => Promise<void>,
  openEventRequest?: { reservationId: number; date?: string } | null,
}) => {
  const { handleEventDrop, handleEventResize } = useCalendarDragAndDrop();
  const types = useAppSelector(selectTypes);
  const user = useAppSelector(selectAuth);
  const rooms = useAppSelector(selectRooms);
  const userId = user.userInfo?.id;

  const handleShowMore = (events: object[], date: Date) => {
    onNavigateRequest(date, 'day' as CalendarSpanEnum);
  };

  //console.log(`Initial events for user ${calenderUserId} in calendar ${instanceKey}:`, events);

  const [isMobile, setIsMobile] = useState(() => window.matchMedia('(max-width: 767px)').matches);

  const stackedView = (grouping !== 'individual') && (span === 'week');
  const columnsView = (grouping !== 'individual') && (span === 'day');
  const agendaView  = (span === 'month' && isMobile);
  const displayName = grouping === "individual" ? false : span === "week" ? true : ownCalendar ? true : true;

  const [infoOpen, setInfoOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editRange, setEditRange] = useState<"this" | "future" | "all" | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<{
    reservation_id?: number;
    event?: ReservationOccurrence;
    start_date?: string;
    start_time?: string;
    end_time?: string;
    facility_id?: number;
  }>({});

  const lastOpenedKeyRef = useRef<string | null>(null);

  events = events ? events.map(event => {
    // Determine if the current user is a participant
    const isParticipant = event.reservation?.participants?.some(p => p.user.id === (calenderUserId || userId)) || false;
    const isRequired = event.reservation?.participants?.some(p => p.user.id === (calenderUserId || userId) && p.required) || false;
    const isPlaceholder = event.reservation?.privacy_placeholder || false;

    // Get reservation type information
    const typeInfo = types?.data?.find(t => t.id === event.type);

    // Set color based on type and participant status
    let hexColor = event.hexColor || typeInfo?.color || "white";
    let textOpacity = 1;

    // Adjust opacity based on required status
    // if (!isRequired && isParticipant) {
    //   // Optional participant: make color lighter
    //   hexColor = adjustColorOpacity(hexColor, 0.6);
    // }

    //   // Non-participant: everything is very light
    // if(!isParticipant) {
    //   hexColor = adjustColorOpacity(hexColor, 0.25);
    //   textOpacity = 0.25;
    // }

    // Cancelled reservations should be grayed out
    if (event.reservation?.cancelled_at) {
      hexColor = "#CCCCCC";
    }

    // Cast isRecurring as boolean
    const isRecurring = !!event.isRecurring;

    // Cast isPrivate as boolean and copy this value from reservation to event
    // (Not strictly nesessary, but nice when styling events)
    const isPrivate = !!event.reservation?.is_private;


    // For recurring events, add a pattern to indicate recurrence - Can also be done in the eventStyleGetter function
    // if (event.recurring_frequency) {
    //   hexColor = `repeating-linear-gradient(45deg, ${hexColor}, ${hexColor} 10px, ${adjustColorOpacity(hexColor, 0.5)} 12px, ${adjustColorOpacity(hexColor, 0.5)} 14px, ${hexColor} 16px)`;
    // }

    // Create title with recurring indicator
    let title = event.title || event.reservation?.description || '';
//    if (event.reservation?.recurring_frequency) {
//      const frequencyLabel = {
//        'daily': '毎日',
//        'weekly': '毎週',
//        'monthly': '毎月',
//        'yearly': '毎年'
//      }[event.reservation?.recurring_frequency] || '';
//      
//      title = `${title} [${frequencyLabel}]`;
//    }
    if (isPlaceholder) { title = "非公開"; }
    if (isPlaceholder) { hexColor = "#ffffdd"; }

    return {
      ...event,
      title: title,
      hexColor,
      opacity: textOpacity,
      isRecurring: isRecurring,
      isPrivate: isPrivate,
      isPlaceholder: isPlaceholder,
    };
  }) : [];

  events = events.filter(event => {
    if (facilityId) {
      return true;
    } else {
      const isParticipant = event.reservation?.participants?.some(p => p.user.id === calenderUserId) || false;
      const isCreator = event.reservation?.creator_id === calenderUserId;
      const isPlaceholder = event.reservation?.privacy_placeholder || false;
      return columnsView || isParticipant || isCreator || isPlaceholder;
    }
  });

  //console.log(`Filtered events for user ${calenderUserId} in calendar ${instanceKey}:`, events);

  if(agendaView && !!events) { events = addPlaceholderEvents(events); }

  useEffect(() => {
    if (!openEventRequest) return;

    const key = `${openEventRequest.reservationId}:${openEventRequest.date ?? ""}`;
    if (lastOpenedKeyRef.current === key) return;

    const targetReservationId = openEventRequest.reservationId;

    const byId = (event: any) => {
      const reservation = event?.reservation;
      const reservationId = reservation?.originalReservation?.id || reservation?.id;
      return Number(reservationId) === targetReservationId;
    };

    const candidates = events.filter(event => !event.isPlaceholder).filter(byId);

    if (candidates.length === 0) {
      console.log("[Schedule] eventId not found:", targetReservationId);
      lastOpenedKeyRef.current = key;
      return;
    }

    let chosen = candidates[0];

    if (openEventRequest.date) {
      const wanted = openEventRequest.date;
      const match = candidates.find(event =>
        moment(event.startDate).format("YYYY-MM-DD") === wanted
      );
      if (match) chosen = match;
    }

    const start_date = moment(chosen.startDate).format("YYYY-MM-DD");

    const reservation = chosen.reservation;
    const reservationId = reservation?.originalReservation?.id || reservation?.id;

    setSelectedSlot({
      reservation_id: reservationId ?? undefined,
      event: chosen,
      start_date,
      start_time: chosen.startTime ?? reservation?.start_time,
      end_time: chosen.endTime ?? reservation?.end_time,
      facility_id: facilityId,
    });
    setInfoOpen(true);

    lastOpenedKeyRef.current = key;
  }, [openEventRequest, events, facilityId]);


  // Keep the "isMobile" variable in sync with the window size
  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 767px)');
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  // Reset scroll position of main container when view changes
  useEffect(() => {
  const el = document.querySelector('#allCalendarElements');
  if (el) {
    el.scrollTop = 0;
    el.scrollLeft = 0;
  }
}, [span, grouping, instanceKey, currentDate, isMobile]);


  // Fade-in state
  const [visible, setVisible] = useState(false);

 // =============================================
 // FADE IN between screens
 // =============================================
  useEffect(() => {

    if (agendaView) {
      setVisible(true);
      return;
    }

    setVisible(false);
    const timeout = setTimeout(() => setVisible(true), 20); // slight delay to trigger CSS transition
    return () => clearTimeout(timeout);
  }, [span, grouping, instanceKey, currentDate, isMobile, agendaView]);

  // Clone top header in group weekly for proper sticky
useEffect(() => {

  const cleanup = domGroupWeekly(
    instanceKey,
    span,
    grouping,
    currentDate,
    events,
    isMobile
  );
  return cleanup;
}, [instanceKey, span, grouping, currentDate, isMobile]); //


// Listen for add button clicks from individual week/day header
useEffect(() => {
  const handleIndividualHeaderAddClick = (e: Event) => {
    const customEvent = e as CustomEvent;
    const eventInstanceKey = customEvent.detail.instanceKey;
    const date = customEvent.detail.date;
    const resourceId = customEvent.detail.resourceId;

    if (!ownCalendar && calendarIndex !== 1) {
      return; // Don't handle the event if not the user's calendar or not the first calendar
    }

    if (eventInstanceKey && !eventInstanceKey.includes(instanceKey.split('-')[1])) {
      return; // The event is from a different calendar view
    }
    
    // Format the selected date
    const start_date = moment(date).format('YYYY-MM-DD');
    
    // Open modal with selected date and default time
    setSelectedSlot({
      reservation_id: undefined,
      event: undefined,
      start_date,
      start_time: '09:00',
      end_time: '10:00',
      facility_id: facilityId,
    });
    setEditOpen(true);
  };

  // Add event listener to the document
  document.addEventListener('individualHeaderAddClick', handleIndividualHeaderAddClick);

  // Cleanup
  return () => {
    document.removeEventListener('individualHeaderAddClick', handleIndividualHeaderAddClick);
  };
}, [facilityId, ownCalendar, calendarIndex, instanceKey]);


useEffect(() => {
  // Listen for add button clicks from group weekly header
  const handleGroupWeekAddClick = (e: Event) => {
    const customEvent = e as CustomEvent;
    const eventInstanceKey = customEvent.detail.instanceKey;
    const date = customEvent.detail.date;

    if (!ownCalendar && calendarIndex !== 1) {
      return; // Don't handle the event if not the user's calendar or not the first calendar
    }

    if (eventInstanceKey && !eventInstanceKey.includes(instanceKey.split('-')[1])) {
      return; // The event is from a different calendar view
    }
    
    // Format the selected date
    const start_date = moment(date).format('YYYY-MM-DD');
    
    // Open modal with selected date and default time
    setSelectedSlot({
      reservation_id: undefined,
      event: undefined,
      start_date,
      start_time: '09:00',
      end_time: '17:00',
      facility_id: facilityId,
    });
    setEditOpen(true);
  };

  // Add event listener to the document
  document.addEventListener('groupWeekAddClick', handleGroupWeekAddClick);

  // Cleanup
  return () => {
    document.removeEventListener('groupWeekAddClick', handleGroupWeekAddClick);
  };
}, [facilityId, ownCalendar, calendarIndex, instanceKey]);


// Listen for add button clicks from agenda view
useEffect(() => {
  const handleAgendaAddClick = (e: Event) => {
    const customEvent = e as CustomEvent;
    const date = customEvent.detail.date;
    const dateString = customEvent.detail.dateString;

    if (!ownCalendar && calendarIndex !== 1) {
      return; // Don't handle the event if not the user's calendar or not the first calendar
    }
    
    // Format the selected date
    const start_date = dateString || moment(date).format('YYYY-MM-DD');
    
    // Open modal with selected date and default time
    setSelectedSlot({
      reservation_id: undefined,
      event: undefined,
      start_date,
      start_time: '09:00',
      end_time: '10:00',
      facility_id: facilityId,
    });
    setEditOpen(true);
  };

  // Add event listener to the document
  document.addEventListener('agendaAddClick', handleAgendaAddClick);

  // Cleanup
  return () => {
    document.removeEventListener('agendaAddClick', handleAgendaAddClick);
  };
}, [facilityId, ownCalendar, calendarIndex]);


// Move time header cell to fix sticky positioning
useEffect(() => {
  const cleanup = domTimeHeaderSticky2(
      instanceKey,
      span,
      grouping,
      currentDate,
      events,
      isMobile,
      calendarIndex
    );
    return cleanup;
}, [instanceKey, grouping, span, calendarIndex, currentDate, events, isMobile]);

// Move time header cell to fix sticky positioning for GROUP
useEffect(() => {
  const cleanup = domGroupDailyNamesHeader(
    instanceKey,
    span,
    grouping,
    currentDate,
    events,
    isMobile
  );
  return cleanup;
}, [instanceKey, span, grouping, currentDate, events, isMobile]);

useEffect(() => {
  if (grouping === "individual") {
  // Find all elements with the specified class
    const stickyHeaders: NodeListOf<Element> = document.querySelectorAll('.sticky-week-header-container');

    // Loop through the found elements and remove them
    stickyHeaders.forEach(header => {
      // Check if parentNode exists before trying to remove the child
      if (header.parentNode) {
        header.parentNode.removeChild(header);
      }
    });

    // Optional: Clean up
    return () => {
      // console.log("Cleanup: Sticky header removal effect cleaned up.");
    };
  } return;
}, [grouping]);


// SUPER IMPORTANT hook: this will make sure all leftover elements are removed when switching from group to individual
useEffect(() => {
  if (grouping === "individual") {
    const cleanupExtraCalendars = () => {
      // 1. Handle the general case: hide all "allCalendarElements2-*" except "allCalendarElements2-1"
      const allCalendars = document.querySelectorAll('div[id^="allCalendarElements2-"]');
      allCalendars.forEach(div => {
        if (div && div.id !== 'allCalendarElements2-1') {
          (div as HTMLElement).style.display = 'none';
          // div.setAttribute('data-removed', 'true');
        }
      });

      // 2. Handle duplicate "allCalendarElements2-1" elements: hide all except the first one
      const specificCalendars = document.querySelectorAll('div[id="allCalendarElements2-1"]');
      if (specificCalendars.length > 1) {
        const lastIndex = specificCalendars.length - 1;
        specificCalendars.forEach((div, index) => {
          if (index < lastIndex) {
            (div as HTMLElement).style.display = 'none';
            // Optional:
            // div.setAttribute('data-removed-duplicate', 'true');
          }
        });
      }
    };

    cleanupExtraCalendars();

    const timeoutId = setTimeout(cleanupExtraCalendars, 200);
    const rafId = requestAnimationFrame(cleanupExtraCalendars);

    return () => {
      clearTimeout(timeoutId);
      cancelAnimationFrame(rafId);
      // Optional: If you were showing elements again on cleanup, you might need to revert display changes.
      // However, this hook specifically hides, so reverting might not be needed unless
      // the 'grouping' changes away from 'individual' and you want to show them again.
      // That would likely be handled by a different effect or conditional rendering.
    };
  }
  // If grouping is NOT "individual", you might want to ensure all relevant calendars are visible.
  // This would typically be handled by React's conditional rendering based on 'grouping'
  // or a separate useEffect hook that shows elements when grouping is not "individual".
  // For example:
  /*
  else {
    // Potentially show elements if they were hidden by this hook previously
    // This depends on how your application manages visibility outside of "individual" grouping.
    const allPotentiallyHiddenCalendars = document.querySelectorAll('div[id^="allCalendarElements2-"][style*="display: none"]');
    allPotentiallyHiddenCalendars.forEach(div => {
        // Be careful here: only revert if this specific hook hid it.
        // This might require more complex state or attributes to track.
        // A simpler approach is to let React manage visibility based on `grouping`.
    });
  }
  */
}, [grouping, instanceKey]);


useEffect(() => {
  // Only run this logic if in agendaView
  if (agendaView) {
    const agendaTableBody = document.querySelector('.rbc-agenda-table tbody');
    if (agendaTableBody) {
      const currentViewMonth = moment(currentDate).month(); // Get the month of the current view (0-indexed)
      let currentGroupIsOutOfMonth = false;

      const rows = agendaTableBody.querySelectorAll('tr');
      rows.forEach(row => {
        const trElement = row as HTMLElement;
        let spanWithDate: Element | null = null;

        // Try to find a span with data-date, often within the specific rbc-agenda-date-cell
        const dateCell = trElement.querySelector('td.rbc-agenda-date-cell');
        if (dateCell) {
          spanWithDate = dateCell.querySelector('span[data-date]');
        }

        // If not found in the typical date cell, check anywhere in the row (as a fallback)
        if (!spanWithDate) {
          spanWithDate = trElement.querySelector('span[data-date]');
        }

        if (spanWithDate) {
          // This row is considered a date header or contains a primary date marker
          const dateStr = spanWithDate.getAttribute('data-date');
          if (dateStr) {
            const rowDate = moment(dateStr, 'YYYY-MM-DD');
            // Check if the date is valid and if its month matches the current view's month
            if (rowDate.isValid() && rowDate.month() !== currentViewMonth) {
              currentGroupIsOutOfMonth = true; // Mark this group of rows as belonging to an outside month
              trElement.style.display = 'none'; // Hide this header row
            } else if (rowDate.isValid()) { // Date is valid and in the current month
              currentGroupIsOutOfMonth = false; // Mark this group as belonging to the current month
              trElement.style.display = ''; // Ensure this header row is visible
            } else {
              // Invalid date string; fall back to visibility based on the previous group's status
              if (currentGroupIsOutOfMonth) {
                trElement.style.display = 'none';
              } else {
                trElement.style.display = '';
              }
            }
          } else {
            // Span exists but has no data-date. Treat as an event row following the current group's visibility.
            if (currentGroupIsOutOfMonth) {
              trElement.style.display = 'none';
            } else {
              trElement.style.display = '';
            }
          }
        } else {
          // This row is likely an event detail row (no direct date marker)
          // Its visibility depends on the preceding date group
          if (currentGroupIsOutOfMonth) {
            trElement.style.display = 'none';
          } else {
            trElement.style.display = '';
          }
        }
      });
    }
  } else {
    // If not in agenda view, ensure all agenda rows (if they persist in the DOM) are visible.
    // This acts as a cleanup if the component switches out of agendaView.
    const agendaTableBody = document.querySelector('.rbc-agenda-table tbody');
    if (agendaTableBody) {
      agendaTableBody.querySelectorAll('tr').forEach(row => {
        (row as HTMLElement).style.display = ''; // Reset display to default
      });
    }
  }

  // Cleanup function: Reset styles when the component unmounts or dependencies change before the next run.
  return () => {
    const agendaTableBody = document.querySelector('.rbc-agenda-table tbody');
    if (agendaTableBody) {
      agendaTableBody.querySelectorAll('tr').forEach(row => {
        (row as HTMLElement).style.display = ''; // Reset display
      });
    }
  };
  // Dependencies for the useEffect hook:
  // - agendaView: The effect runs based on this flag.
  // - currentDate: Determines the current month for comparison.
  // - events: Agenda rows are generated from events; changes should trigger the effect.
  // - isMobile: agendaView depends on isMobile state.
  // - span: agendaView also depends on the calendar span.
}, [agendaView, currentDate, events, isMobile, span]);


// =====================================================
// SCROLL AGENDA VIEW TO CURRENT DATE
// =====================================================

useEffect(() => {
  // Only run if we're in agenda view
  if (!agendaView) return;

  // Give the agenda view time to render
  const scrollToToday = () => {
    // Check if we're viewing the current month
    const currentMonth = moment(currentDate).format('YYYY-MM');
    const todayMonth = moment().format('YYYY-MM');
    const isCurrentMonth = currentMonth === todayMonth;
    
    // Find the scrollable container first
    let scrollableContainer: HTMLElement | null = null;
    
    // Try to find from the agenda table
    const agendaTable = document.querySelector('.rbc-agenda-table');
    if (agendaTable) {
      let currentElement: HTMLElement | null = agendaTable as HTMLElement;
      
      while (currentElement && !scrollableContainer) {
        currentElement = currentElement.parentElement;
        if (currentElement) {
          const style = window.getComputedStyle(currentElement);
          const overflow = style.overflow + style.overflowY;
          
          if (overflow.includes('scroll') || overflow.includes('auto')) {
            if (currentElement.scrollHeight > currentElement.clientHeight) {
              scrollableContainer = currentElement;
            }
          }
        }
      }
    }
    
    // Fallback to known containers
    if (!scrollableContainer) {
      const fallbackContainers = [
        document.getElementById('allCalendarElements'),
        document.querySelector('[id^="allCalendarElements"]'),
        document.querySelector('.rbc-agenda-view')?.parentElement
      ];
      
      for (const container of fallbackContainers) {
        if (container && container instanceof HTMLElement && container.scrollHeight > container.clientHeight) {
          scrollableContainer = container;
          break;
        }
      }
    }
    
    if (!scrollableContainer) return;
    
    // If not current month, just scroll to top
    if (!isCurrentMonth) {
      scrollableContainer.scrollTop = 0;
      return;
    }
    
    // For current month, find and scroll to today
    const dateCells = document.querySelectorAll('span[data-date]');
    
    if (dateCells.length === 0) return;
    
    // Get today's date in YYYY-MM-DD format
    const todayStr = moment().format('YYYY-MM-DD');
    
    // Find today's date cell and row
    let todayRow: HTMLElement | null = null;
    
    for (let i = 0; i < dateCells.length; i++) {
      const cell = dateCells[i];
      if (cell.getAttribute('data-date') === todayStr) {
        let parent: HTMLElement | null = cell as HTMLElement;
        while (parent && parent.tagName !== 'TR') {
          parent = parent.parentElement;
        }
        if (parent) {
          todayRow = parent;
          break;
        }
      }
    }
    
    if (!todayRow) {
      // If today is not found (might be filtered), scroll to top
      scrollableContainer.scrollTop = 0;
      return;
    }
    
    try {
      // Force a layout recalculation before getting positions
      void todayRow.offsetHeight;
      void scrollableContainer.offsetHeight;
      
      // Calculate the position of the row relative to the scrollable container
      const rowRect = todayRow.getBoundingClientRect();
      const containerRect = scrollableContainer.getBoundingClientRect();
      
      // Calculate target scroll position
      const targetScroll = scrollableContainer.scrollTop + (rowRect.top - containerRect.top) - 0;
      
      // Perform the scroll
      scrollableContainer.scrollTop = targetScroll;
      
      // Double-check and adjust if needed
      setTimeout(() => {
        // Recalculate in case positions changed
        const newRowRect = todayRow.getBoundingClientRect();
        const newContainerRect = scrollableContainer.getBoundingClientRect();
        const newTargetScroll = scrollableContainer.scrollTop + (newRowRect.top - newContainerRect.top) - 0;
        
        // If the position is significantly different, adjust
        if (Math.abs(newTargetScroll - scrollableContainer.scrollTop) > 5) {
          scrollableContainer.scrollTop = newTargetScroll;
        }
      }, 100);
      
    } catch (error) {
      console.error('[Agenda Scroll] Error:', error);
    }
  };
  
  // Clear any existing scroll position first when switching months
  const resetAndScroll = () => {
    const container = document.getElementById('allCalendarElements');
    if (container) {
      // Reset to top first
      container.scrollTop = 0;
      // Then perform the actual scroll logic
      setTimeout(scrollToToday, 50);
    } else {
      scrollToToday();
    }
  };
  
  // Run with multiple attempts for robustness
  const timeout1 = setTimeout(resetAndScroll, 100);
  const timeout2 = setTimeout(scrollToToday, 300);
  const timeout3 = setTimeout(scrollToToday, 500);
  
  // Cleanup
  return () => {
    clearTimeout(timeout1);
    clearTimeout(timeout2);
    clearTimeout(timeout3);
  };
}, [agendaView, currentDate, events]); // Added events to dependencies



  // Adds "なし" events for dates with no events in the current month
  function addPlaceholderEvents(sparseEvents) {
    // Get first and last day of current month
    const firstDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    const lastDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);

    // Create a map to track dates that already have events
    const datesWithEvents = new Map<string, boolean>();
    
    // Mark dates that already have events (including all days for multi-day events)
    sparseEvents.forEach(event => {
      // Create date objects to iterate from start to end
      const startDate = new Date(event.startDate);
      const endDate = new Date(event.endDate);

      // Reset time portions to compare dates only
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(0, 0, 0, 0);

      // For each day from start to end (inclusive)
      const currentDay = new Date(startDate);
      while (currentDay <= endDate) {
        const dateStr = `${currentDay.getFullYear()}-${currentDay.getMonth() + 1}-${currentDay.getDate()}`;
        datesWithEvents.set(dateStr, true);
        
        // Move to next day
        currentDay.setDate(currentDay.getDate() + 1);
      }
    });
    
    // Create a new array with the existing calendar inputs
    const updatedCalendarInputs = [...sparseEvents];
    
    // If we have at least one calendar to add events to
    // if (updatedCalendarInputs.length > 0) {
    const day = new Date(firstDayOfMonth);
      // For each day in the current month
      while (day <= lastDayOfMonth) {
       const dateStr = `${day.getFullYear()}-${day.getMonth() + 1}-${day.getDate()}`;
        
        // If this date doesn't have any events
        if (!datesWithEvents.has(dateStr)) {
          // Create a new date object to avoid reference issues
          const currentDay = new Date(day);
          
          // Add the "なし" event
          updatedCalendarInputs.push({
            startDate: currentDay,
            endDate: currentDay,
            title: "なし",
            hexColor: "#ffffff88",
            reservation: null,
            allDay: true,
            isRecurring: false,
            emptyEvent: true,
       });
    }
    day.setDate(day.getDate() + 1);
  }
    // Return the updated array or update state directly
    return updatedCalendarInputs;
  };

const onSelectSlot = (slotInfo: any, facilityId?: number) => {
  //console.log("Selected slot:", slotInfo, "Facility:", facilityId);
    
    // Format the start date and time
    const start_date = moment(slotInfo.start).format('YYYY-MM-DD');
    
    // Set default time range based on the view
    let start_time, end_time;
    
    if (span === 'day' || span === 'week') {
      // For day or week view, use the exact time selected
      start_time = moment(slotInfo.start).format('HH:mm');
      end_time = moment(slotInfo.end).format('HH:mm');
    } else {
      // For month view, default to a morning meeting
      start_time = '09:00';
      end_time = '10:00';
    }
    
    // Open modal with selected date and time
    setSelectedSlot({ start_date, start_time, end_time, facility_id: facilityId });
    setEditOpen(true);
  };
  
  const onSelectEvent = (event: any) => {
    //console.log("Selected event:", event);
    const reservation = event.reservation;
    const reservationId = reservation.originalReservation?.id || reservation.id;
  
    setSelectedSlot({
      start_date: reservation.start_date,
      start_time: reservation.start_time,
      end_time: reservation.end_time,
      reservation_id: reservationId,
      event: event,
    });
  
    setInfoOpen(true);
  };


  // Content for all events
  function Event({ event, title }: any) {
    const ref = React.useRef(null);    
    const isRecurring = event.isRecurring;
    const isPrivate = event.isPrivate;
    const isPlaceholder = event.isPlaceholder;

    let borderRadius:(string | number | null) = null;

    if (event.emptyEvent) {
      borderRadius = 0;
    }

    // is event multiday or not (no end date means recurring forever)
    const hasEndDate = !!event.endDate;
    const isMultiDay = (hasEndDate && !moment(event.startDate).isSame(moment(event.endDate), 'day'));

    let timeLabel = "";

    if (isMultiDay) {
        const startDateTimeStr = moment(event.startDate).format("M/D H:mm");
        const endDateTimeStr = moment(event.endDate).format("M/D H:mm");
        timeLabel = `${startDateTimeStr} - ${endDateTimeStr}`;
    } else {
        if (event.startTime && event.endTime) {
            timeLabel =
            `${moment(event.startTime, "HH:mm:ss").format("H:mm")} - ${moment(event.endTime, "HH:mm:ss").format("H:mm")}`;
        } else if (event.startTime) {
            timeLabel = moment(event.startTime, "HH:mm:ss").format("H:mm");
        }
    }


    return (
      <div
        ref={ref}
        style={{
          display: 'flex',
          flexDirection: 'column',     
          alignItems: 'flex-start',     
          opacity: 1,               
          borderRadius: borderRadius || "1rem",
          height: '100%',
          overflow: 'hidden',
        }}
      >
        {timeLabel && (
          <div
            style={{
              fontSize: '12px',
              fontWeight: "bold",
              color: '#444',
              marginBottom: '0px',
              marginTop: "0px",
              whiteSpace: 'normal',  
              wordBreak: 'break-word', 
              lineHeight: '1.2',  
              maxWidth: '100%',  
              padding: '1px 0px', 
              display: "flex",
              flexDirection: "row"
            }}
          >
            {isPrivate && !isPlaceholder && (
              <PrivateEventMark scale={0.85} />
            )}
            {isRecurring && (
            <div><IconRepeat
            style={{
              width:"14px",
              height:"14px",
              marginRight:"4px",
              marginTop:"1px",
              }}/></div>
            )}
              <div>{timeLabel}</div>
          </div>
        )}

        <span
          style={{
            fontWeight: "normal",
            color: "#444",
            fontSize: "12px",
            lineHeight: "16px",
            whiteSpace: (span === "month") ? "nowrap" : "nowrap",
            overflow: (span === "month") ? "hidden" : "hidden",
            textOverflow: (span === "month") ? "ellipsis" : "ellipsis",
            maxWidth: "100%",
            display: "block",
            paddingBottom: "5x",
            marginTop: "0px"
          }}
        >
          {title}
        </span>
      </div>
      );
  }


  // Style for all events
  function stripAlpha(hex) {
  if (/^#([0-9a-fA-F]{8})$/.test(hex)) {
      return '#' + hex.slice(1, 7);
    }
    return hex;
  }
  const eventStyleGetter = (event: any) => {
    let backgroundColor = event.hexColor || "#90ee90";
    const isRecurring = event.isRecurring;
    backgroundColor = stripAlpha(backgroundColor);
    const style = {
      background: backgroundColor,
      borderRadius: "6px",
      opacity: 1,
      color: "black",
      border: "0",
      height: agendaView ? "57px" : "unset",
      borderBottom: agendaView ? "1px solid #ddd" : "unset",
      display: agendaView ? "inline-flex" : "block",
      fontSize:"12px",
      fontWeight:"normal",
      width: "calc(100% - 6px)",
      boxSizing: "border-box" as React.CSSProperties["boxSizing"],
      margin: "1px 3px",
    };
      
    return { style };
  };


  // Style for each day
  const dayPropGetter = (date: Date) => {
    const formatted = moment(date).format("YYYY-MM-DD");
  
    const isHoliday = holidays.some(h => h === formatted);
    const day = date.getDay(); // 0 = Sunday, 6 = Saturday
  
   let backgroundColor = "white";
  
    if (isHoliday) {
      backgroundColor = "#f1ffee"; // Light green for holidays
    } else if (day === 6) {
      backgroundColor = "#f3f3f3";
    } else if (day === 0) {
      backgroundColor = "#f3f3f3"; // sunday
    }
  
    return {
      style: {
        backgroundColor,
      },
    };
  };

  // CustomDateHeader
  const CustomDateHeader = ({ label, date }: any) => {
    const handleAddClick = (e: React.MouseEvent) => {
      e.stopPropagation(); // prevent triggering other calendar clicks
      //console.log("Add clicked for", date);

      // Format the selected date
      const start_date = moment(date).format('YYYY-MM-DD');

      // Open modal with selected date, and default time range
      setSelectedSlot({
        reservation_id: undefined,
        event: undefined,
        start_date,
        start_time: '09:00',
        end_time: '10:00',
        facility_id: facilityId,
      });
      setEditRange(null); // we're making a new reservation, so clear edit range
      setEditOpen(true);
    };

    // Check if the date is today
    const isToday = moment(date).isSame(moment(), 'day');
  
    return (
      <div style={{ position: "relative", minHeight:"30px"}}>
        <span className={isToday ? "date-is-today" : ""}>
          {parseInt(label, 10)}
        </span>
        <button
          onClick={handleAddClick}
          style={{
            position: "absolute",
            top: 5,
            left: 5,
            zIndex: 10,
            background: "transparent",
            color: "#ff6a0c",
            border: "none",
            borderRadius: "50%",
            width: "18px",
            height: "18px",
            fontSize: "24px",
            fontWeight: "bold",
            lineHeight: "14px",
            cursor: "pointer",
          }}
        >
          +
        </button>
      </div>
    );
  };

  // CustomWeekDateHeader
  const CustomWeekDateHeader = ({ label, date }: any) => {
  const handleAddClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    console.log("Add clicked for week view", date);
    
    // Format the selected date
    const start_date = moment(date).format('YYYY-MM-DD');
    
    // Open modal with selected date and default all-day time
    setSelectedSlot({
      reservation_id: undefined,
      event: undefined,
      start_date,
      start_time: '09:00',
      end_time: '17:00', // Default to full day for all-day events
      facility_id: facilityId,
    });
    setEditOpen(true);
  };

  const isToday = moment(date).isSame(moment(), 'day');

  // Format the label to show day of week and date
   const formattedLabel = `${moment(date).format('M/D')} (${moment(date).format('dd')})`;

  return (
    <div style={{ 
      position: "relative", 
      minHeight: "30px",
      width: "100%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 1,
      flexBasis: "100%",
      fontWeight: "bold",
    }}>
      <span className={isToday ? "date-is-today-header" : ""}>{formattedLabel}</span>
      <button
        onClick={handleAddClick}
        style={{
          position: "absolute",
          left: 5,
          top:7,
          zIndex: 10,
          background: "transparent",
          color: "#ff6a0c",
          border: "none",
          borderRadius: "50%",
          width: "18px",
          height: "18px",
          fontSize: "24px",
          fontWeight: "bold",
          lineHeight: "14px",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          pointerEvents: "auto",
        }}
      >
        +
      </button>
    </div>
  );
};

const CustomDayDateHeader = ({ label, date }: any) => {
  const handleAddClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    console.log("Add clicked for day view", date);
    
    // Format the selected date
    const start_date = moment(date).format('YYYY-MM-DD');
    
    // Open modal with selected date and default all-day time
    setSelectedSlot({
      reservation_id: undefined,
      event: undefined,
      start_date,
      start_time: '09:00',
      end_time: '17:00', // Default to full day for all-day events
      facility_id: facilityId,
    });
    setEditOpen(true);
  };

  // Format the label to show full date with Japanese day of week
  const dayOfWeek = ['日', '月', '火', '水', '木', '金', '土'][moment(date).day()];
  const formattedLabel = `${moment(date).format('M/D')} (${dayOfWeek})`;

  return (
    <div style={{ 
      position: "relative", 
      minHeight: "30px",
      width: "100%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 1,
      flexBasis: "100%",
      fontWeight: "bold"
    }}>
      {formattedLabel}
      <button
        onClick={handleAddClick}
        style={{
          position: "absolute",
          left: 5,
          top:7,
          zIndex: 10,
          background: "transparent",
          color: "#ff6a0c",
          border: "none",
          borderRadius: "50%",
          width: "18px",
          height: "18px",
          fontSize: "24px",
          fontWeight: "bold",
          lineHeight: "14px",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          pointerEvents: "auto",
        }}
      >
        +
      </button>
    </div>
  );
};

  const ReservationFormDialog = (
    <Dialog
      open={infoOpen || editOpen}
      onClose={() => { setInfoOpen(false); setEditOpen(false); }}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          ...(!isMobile && { borderRadius: '10px' }),
          padding: "3px",
          overflowX: "hidden"
        },
      }}
    >
      <DialogContent style={{ padding: '0', paddingTop:"4rem"}}>
        {infoOpen ? (
          <ReservationInfo 
            reservationId={selectedSlot.reservation_id || 0}
            reservationOccurrence={selectedSlot.event}
            handleClose={() => {setInfoOpen(false);}}
            handleEdit={(range) => {setInfoOpen(false); setEditOpen(true); setEditRange(range);}} 
          />
        ) : editOpen ? (
          <ReservationForm 
            isModal={true} 
            onClose={() => setEditOpen(false)} 
            initialData={selectedSlot} 
            editRange={editRange}
            reservationOccurrence={selectedSlot.event}
            defaultParticipantIds={defaultParticipants} 
            refreshReservations={refreshReservations}
            defaultParticipantsHistory={defaultParticipantsHistory}
            storeDefaultParticipantsSelection={storeDefaultParticipantsSelection}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );

  const firstDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
  const monthLength = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();


const filterEventsForAgendaView = (events: any[]) => {
  if (!agendaView) return events;
  
  const monthStart = moment(currentDate).startOf('month');
  const monthEnd = moment(currentDate).endOf('month');
  
  return events.map(event => {
    const eventStart = moment(event.startDate);
    const eventEnd = moment(event.endDate);
    
    // If event is completely outside the current month, exclude it
    if (eventEnd.isBefore(monthStart) || eventStart.isAfter(monthEnd)) {
      return null;
    }
    
    // If event spans multiple months, truncate it to current month boundaries
    const adjustedEvent = { ...event };
    
    // If event starts before this month, adjust start to first day of month
    if (eventStart.isBefore(monthStart)) {
      adjustedEvent.startDate = monthStart.toDate();
      adjustedEvent.startTime = "00:00:00";
    }
    
    // If event ends after this month, adjust end to last day of month
    if (eventEnd.isAfter(monthEnd)) {
      adjustedEvent.endDate = monthEnd.toDate();
      adjustedEvent.endTime = "23:59:59";
    }
    
    return adjustedEvent;
  }).filter(event => event !== null);
};

  //console.log(`Final events for user ${calenderUserId}: `, events);

  return (
    <>
      {ReservationFormDialog}
      <div

      id={`allCalendarElements2-${calendarIndex}`}
      key={instanceKey}

      style={{
        display: "flex",
        flex: "1",
        flexDirection: columnsView ? "column" : "row",
        alignItems: "start",
        minWidth: span === "week" ? "1000px" : "200px",
        width: "100%",
        height: stackedView ? undefined : "100%",
        }}>

        <div id={`nameCell-${calendarIndex}`}
         style={{
          width: isMobile
            ? (span === "week" ? "100px" : "200px")  // mobile
            : (span === "week" ? "150px" : "200px"), // desktop
          height: isMobile
            ? (span === "week" ? "100%" : "30px")  // mobile
            : (span === "week" ? "100%" : "50px"), // desktop
          minWidth: isMobile
            ? (span === "week" ? "100px" : "200px") // mobile
            : (span === "week" ? "150px" : "200px"), // desktop
          minHeight: isMobile
            ? (span === "week" ? "150px" : "30px") // mobile
            : (span === "week" ? "150px" : "50px"), // desktop
          flexShrink: 0,
          background:"#e4e4e4",
          display: (span==="week" && displayName) ? "flex" : "none",
          alignItems: "start",
          justifyContent: span === "week" ? "start" : "center",
          paddingLeft:"1rem",
          paddingTop:"1rem",
          borderRight:"1px solid #bbb",
          borderBottom:"1px solid #bbb",
          position:"sticky",
          left: span === "week" ? "0" : "unset",
          top: span === "day" ? "0" : "unset",
          zIndex: span === "week" ? 9 : 12,
      }}>
            {(stackedView || columnsView) ? name : ""}
          </div>
        <StyledAppCalendar id="styledAppCalendar" className={stackedView ? 'row-mode' : 'full-mode'}
        $span={span} $grouping={grouping}
        >
          <DragAndDropCalendar
            className={`fade-in${visible ? " visible" : ""}`}
            localizer={localizer}
            style={{ width: columnsView ? "100%" : "100%" }}
            resources={resources}
            events={events}
            startAccessor={(event) => event.startDate}
            endAccessor={(event) => event.endDate}
            components={{
              event: Event,
              toolbar: blankToolbar,
              header: (ownCalendar) ? undefined : blankHeader,
              month: {
                event: Event,
                dateHeader: (span === 'month') ? CustomDateHeader : undefined }, // the little plus button on the top left of each day
              agenda: {
                date: makeCustomAgendaDate(holidays),
                event: makeCustomAgendaEvent(holidays),
              },
              week: {
                header: CustomWeekDateHeader,
                event: Event
              },
              day: {
                header: CustomDayDateHeader,
                event: Event
              },
            }}
            selectable
            resizable
            draggableAccessor={(event) => !event.isPlaceholder} // placeholder events not draggable
            onEventDrop={(event) => {
              if (event.event.isPlaceholder) return;
              const conflict: ReservationConflict = detectConflict(event.start, event.end, types.data, (event.event as any), null, events);
              if (conflict.hasConflict) {
                const conflictRoomNames = conflict.roomIds.map(id => {
                  const room = rooms.data.find(r => r.id === id);
                  return room ? room.name : `部屋 ${id}`;
                }).join(", ");
                // This event can't be moved due to the following rooms being unavailable at this time: 1, 2, 3
                alert(`施設の予約がすでにあるため変更できません。\n該当の施設:${conflictRoomNames}。`);
                return;
              }
              handleEventDrop(event);
            }}
            onEventResize={(event) => {
              if (event.event.isPlaceholder) return;
              const conflict: ReservationConflict = detectConflict(event.start, event.end, types.data, (event.event as any), null, events);
              if (conflict.hasConflict) {
                const conflictRoomNames = conflict.roomIds.map(id => {
                  const room = rooms.data.find(r => r.id === id);
                  return room ? room.name : `部屋 ${id}`;
                }).join(", ");
                // This event can't be resized due to the following rooms being unavailable at this time: 1, 2, 3
                alert(`施設の予約がすでにあるため変更できません。\n該当の施設:${conflictRoomNames}。`);
                return;
              }
              handleEventResize(event);
            }}
            onSelectSlot={(slotInfo) => onSelectSlot(slotInfo, facilityId)}
            onSelectEvent={(event) => {
              if (event.isPlaceholder) return;
              onSelectEvent(event);
            }}
            eventPropGetter={eventStyleGetter}
            dayPropGetter={dayPropGetter}
            views={["month", "week", "day", "agenda"]}
            view={stackedView ? "month" : agendaView ? "agenda" : span } 
            date={agendaView ? firstDayOfMonth : currentDate}
            length={monthLength}
            showAllEvents={true}
            onShowMore={handleShowMore}
            onNavigate={(newDate, newView, action) => {
                 onNavigateRequest(newDate, newView as CalendarSpanEnum, action);
            }}
          />
        </StyledAppCalendar>
      </div>
    </>
  );
};

const blankToolbar = () => {
  return null;
};

const blankHeader = () => {
  return null;
};

const adjustColorOpacity = (hexColor: string, opacity: number): string => {
  // incoming hex
  const redHex = hexColor.slice(1, 3);
  const greenHex = hexColor.slice(3, 5);
  const blueHex = hexColor.slice(5, 7);
  // Convert hex to RGB
  const r = parseInt(redHex, 16);
  const g = parseInt(greenHex, 16);
  const b = parseInt(blueHex, 16);
  
  // Create RGB with opacity
  //return `rgba(${r}, ${g}, ${b}, ${opacity})`;

  // rgba means you can't run this function twice, so use hex with opacity at the end
  const hexOpacity = Math.round(opacity * 255).toString(16).padStart(2, '0');
  return `#${redHex}${greenHex}${blueHex}${hexOpacity}`;
};

